import { WaypointsFullResponseItem } from './IAeroInfoRepository'

/**
 * Campos de `extra` do ADHP descartados do `waypoints_full`.
 *
 * Medido no payload real: `rotaerHTML` sozinho são 12,56 MB dos 24,95 MB, e o conjunto abaixo soma
 * **15,19 MB (61%)**. Nada disso é usado pelo mapa nem pela busca — o único consumidor é o módulo
 * `AdhpInfo`, que já busca o ADHP completo por ICAO (`refreshADHPData` → GraphQL) toda vez que um
 * aeródromo é aberto. Carregar no boot era pagar download, parse, mapeamento e memória residente por
 * dado que só aparece se o usuário abrir a tela.
 *
 * `rotaer` (3,01 MB) NÃO entra aqui: `MapADHP/GeojsonUtils.ts` deriva os filtros da camada de pontos
 * (militar / ATS / comprimento e superfície de pista / IFR / noturno) de `rotaer.typeUtil`,
 * `rotaer.communications`, `rotaer.runways` e `rotaer.typeOpr` para **todos** os 6.071 ADHPs, e
 * `TrackHeadboardPresenter` também usa `rotaer.runways`.
 */
export const OMITTED_ADHP_EXTRA_FIELDS = [
  'rotaerHTML',
  'infotemp',
  'aipsup',
  'notam',
  'meteorology',
  'metarHistory',
  'daytime',
  'charts'
] as const

export interface TrimmedWaypointsFull {
  items: WaypointsFullResponseItem[]
  itemCount: number
  /**
   * Carimbo para detectar mudança de conteúdo sem depender de ETag.
   *
   * O `aerodata` não expõe `ETag` de forma legível por JS (não é header CORS-safelisted e a origem não
   * manda `Access-Control-Expose-Headers`) nem aceita `If-None-Match` (viraria preflight). Então o
   * carimbo é derivado do próprio payload: contagem + maior `updatedAt`.
   */
  sourceStamp: string
}

/**
 * Remove os campos pesados e calcula o carimbo de origem numa **única** passada.
 *
 * Muta o objeto `extra` recebido em vez de clonar: o payload acabou de sair do `JSON.parse` e não é
 * compartilhado com ninguém, então clonar 20.780 itens só para descartar campos seria pagar de novo o
 * custo que esta função existe para evitar.
 */
export function trimWaypointsFull(rawItems: WaypointsFullResponseItem[]): TrimmedWaypointsFull {
  let maxUpdatedAt = 0

  for (const item of rawItems) {
    const updatedAtMs = Date.parse(item.updatedAt as unknown as string)
    if (Number.isFinite(updatedAtMs) && updatedAtMs > maxUpdatedAt) {
      maxUpdatedAt = updatedAtMs
    }

    if (item.__typename !== 'ADHP' || !item.extra) {
      continue
    }

    for (const field of OMITTED_ADHP_EXTRA_FIELDS) {
      if (field in item.extra) {
        delete (item.extra as Record<string, unknown>)[field]
      }
    }
  }

  return {
    items: rawItems,
    itemCount: rawItems.length,
    sourceStamp: `${rawItems.length}:${maxUpdatedAt}`
  }
}
